/**
 * Coverage computation for the service-agreement matrix.
 *
 * Determines whether every place a service is offered has an agreement
 * that will resolve when a customer books from that jurisdiction.
 */

import type { BookingTypeRecord } from "./catalog/types";
import type { Agreement } from "./AgreementCard";

/* ── Cell status ─────────────────────────────────────────────────── */

export type CellStatus = "covered" | "partial" | "gap" | "na";

export interface CoverageCell {
  status: CellStatus;
  /** Name of the resolving agreement, if any (for tooltip). */
  resolvingAgreement?: string;
}

/* ── Row: one active service ──────────────────────────────────────── */

export interface CoverageRow {
  serviceId: string;
  serviceName: string;
  /** AgreementType id (preferred) or raw agreementType slug. */
  agreementTypeId: string | null;
  /** Human-readable document label (AgreementType name). */
  documentLabel: string;
  /** Set of states this service is offered in. */
  offeredIn: ReadonlySet<string>;
  /** Cell per jurisdiction column. */
  cells: Record<string, CoverageCell>;
}

/* ── Full result ─────────────────────────────────────────────────── */

export interface CoverageResult {
  /** Sorted jurisdiction column headers. */
  columns: string[];
  /** One row per active service, sorted by name. */
  rows: CoverageRow[];
  /** Total number of "gap" cells (the actionable count). */
  gapCount: number;
  /** Number of services that have at least one gap. */
  servicesWithGaps: number;
}

/* ── Humanize ────────────────────────────────────────────────────── */

/** Underscore-aware title-case, matching the local convention. */
export function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ── Core computation ────────────────────────────────────────────── */

/**
 * Compute coverage keyed on `agreementTypeId` (the first-class entity).
 * Falls back to the legacy `agreementType` string when no id is present.
 *
 * @param typeIdToName  Map from AgreementType id to its display name,
 *                      used to label rows and resolve the document family.
 */
export function computeCoverage(
  services: readonly BookingTypeRecord[],
  agreements: readonly Agreement[],
  generalStates: readonly string[],
  typeIdToName?: ReadonlyMap<string, string>,
  typeKeyToId?: ReadonlyMap<string, string>,
): CoverageResult {
  const activeServices = services.filter((s) => s.isActive);
  const activeAgreements = agreements.filter((a) => a.isActive);

  // Canonical document key: always resolve to the AgreementType id when we can,
  // mapping a legacy string through typeKeyToId, so a service keyed by id and an
  // agreement keyed only by the string (or vice versa, mid-transition) land on
  // the same key instead of splitting into a false gap.
  const canonKey = (id: string | null, str: string | null): string =>
    id || (str ? (typeKeyToId?.get(str) ?? str) : "");

  // Build the column set: generalStates + service states + agreement states.
  const columnSet = new Set<string>(generalStates);
  for (const s of activeServices) {
    if (s.state) columnSet.add(s.state);
  }
  for (const a of activeAgreements) {
    if (a.jurisdictionKind === "state" && a.state) columnSet.add(a.state);
  }
  const columns = Array.from(columnSet).sort();

  // Index active agreements by key (agreementTypeId preferred, fallback to string).
  const byKey = new Map<string, Agreement[]>();
  for (const a of activeAgreements) {
    const key = canonKey(a.agreementTypeId, a.agreementType);
    if (!key) continue;
    const list = byKey.get(key);
    if (list) list.push(a);
    else byKey.set(key, [a]);
  }

  let gapCount = 0;
  const gapServiceIds = new Set<string>();

  const rows: CoverageRow[] = activeServices
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((svc) => {
      const offeredIn: Set<string> = svc.state
        ? new Set([svc.state])
        : new Set(generalStates);

      const cells: Record<string, CoverageCell> = {};

      // Key the service's document canonically (see canonKey), so it matches
      // agreements regardless of which side carries the id vs the legacy string.
      const svcKey = canonKey(svc.agreementTypeId, svc.agreementType);

      for (const col of columns) {
        if (!offeredIn.has(col)) {
          cells[col] = { status: "na" };
          continue;
        }

        if (!svcKey) {
          cells[col] = { status: "gap" };
          gapCount++;
          gapServiceIds.add(svc.id);
          continue;
        }

        const candidates = byKey.get(svcKey) ?? [];
        let status: CellStatus = "gap";
        let resolvingName: string | undefined;

        for (const ag of candidates) {
          if (ag.jurisdictionKind === "national") {
            status = "covered";
            resolvingName = ag.name;
            break;
          }
          if (ag.jurisdictionKind === "state" && ag.state === col) {
            status = "covered";
            resolvingName = ag.name;
            break;
          }
        }

        if (status === "gap") {
          // Check for sub-state coverage (county, city, zip).
          for (const ag of candidates) {
            const subState =
              ag.jurisdictionKind === "county" ||
              ag.jurisdictionKind === "city" ||
              ag.jurisdictionKind === "zip";
            if (subState && ag.state === col) {
              status = "partial";
              resolvingName = ag.name;
              break;
            }
          }
        }

        if (status === "gap") {
          gapCount++;
          gapServiceIds.add(svc.id);
        }

        cells[col] = { status, resolvingAgreement: resolvingName };
      }

      // Resolve document label: AgreementType name > humanized string > empty
      let documentLabel = "";
      if (svc.agreementTypeId && typeIdToName?.has(svc.agreementTypeId)) {
        documentLabel = typeIdToName.get(svc.agreementTypeId)!;
      } else if (svc.agreementType) {
        documentLabel = humanize(svc.agreementType);
      }

      return {
        serviceId: svc.id,
        serviceName: svc.name,
        agreementTypeId: svc.agreementTypeId ?? null,
        documentLabel,
        offeredIn,
        cells,
      };
    });

  return {
    columns,
    rows,
    gapCount,
    servicesWithGaps: gapServiceIds.size,
  };
}
